import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { FaArrowAltCircleUp } from "react-icons/fa";
import "./ThreatIntelligenceV2.css";

const DOMAIN_COLORS = {
  "Misinformation": "#5e0e20",
  "Human-Computer Interaction": "#8a7c7c",
  "Privacy & Security": "#3e0d15",
  "Discrimination & Toxicity": "#26070c",
  "Malicious Actors & Misuse": "#5b5452",
  "AI System Safety, Failures, & Limitations": "#85192e",
  "Socioeconomic and Environmental": "#a31e38",
};

const DOMAIN_COLORS2 = {
  "Discrimination & Toxicity": "#652f90",
  "Privacy & Security": "#813cb9",
  "Misinformation": "#9556c8",
  "Malicious Actors & Misuse": "#a874d2",
  "Human-Computer Interaction": "#c5a3e1",
  "Socioeconomic and Environmental": "#d8c1eb",
  "AI System Safety, Failures, & Limitations": "#ece0f5",
};

const DOMAIN_COLORS3 = {
  "Discrimination & Toxicity": "#392d69",
  "Privacy & Security": "#4e3a7f",
  "Misinformation": "#624795",
  "Malicious Actors & Misuse": "#7754ac",
  "Human-Computer Interaction": "#8c61c2",
  "Socioeconomic and Environmental": "#9c69cf",
  "AI System Safety, Failures, & Limitations": "#a06ed8",
};

const DOMAIN_COLORS4 = {
  "Discrimination & Toxicity": "#392d69",
  "Privacy & Security": "#4e3a7f",
  "Misinformation": "#624795",
  "Malicious Actors & Misuse": "#7e6f90",
  "Human-Computer Interaction": "#8c7e9c",
  "Socioeconomic and Environmental": "#7754ac",
  "AI System Safety, Failures, & Limitations": "#8c61c2",
};


const DOMAIN_ORDER = [
  "1. Discrimination & Toxicity",
  "2. Privacy & Security",
  "3. Misinformation",
  "4. Malicious Actors & Misuse",
  "5. Human-Computer Interaction",
  "6. Socioeconomic and Environmental",
  "7. AI System Safety, Failures, & Limitations",
];

function normalizeDomainName(domain) {
  return domain.replace(/^\d+\.\s*/, "");
}

function sortSubdomains(subdomains) {
  const extractNumeric = (label) => {
    const match = label.match(/^\d+(\.\d+)?/);
    return match ? match[0].split('.').map(Number) : [0];
  };

  return subdomains.sort((a, b) => {
    const numsA = extractNumeric(a);
    const numsB = extractNumeric(b);

    for (let i = 0; i < Math.max(numsA.length, numsB.length); i++) {
      const diff = (numsA[i] || 0) - (numsB[i] || 0);
      if (diff !== 0) return diff;
    }
    return 0;
  });
}

const AIRiskDatabase = () => {
  const [data, setData] = useState([]);
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [expandedSubdomain, setExpandedSubdomain] = useState(null);

  useEffect(() => {
    fetch("/AI_ThreatIntelligence.xlsx")
      .then((res) => res.arrayBuffer())
      .then((arrayBuffer) => {
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheet = workbook.Sheets["AI Risk Database v3"];
        if (!sheet) throw new Error("Sheet 'AI Risk Database v3' not found");

        const rawData = XLSX.utils.sheet_to_json(sheet);
        const cleanedData = rawData.map((row, index) => ({
          id: index,
          title: row["Title"] || "N/A",
          riskCategory: row["Risk category"] || "",
          description: row["Description"] || "",
          evidence: row["Additional ev."] || "",
          entity: row["Entity"] || "",
          intent: row["Intent"] || "",
          timing: row["Timing"] || "",
          domain: row["Domain"] || "",
          subDomain: row["Sub-domain"] || "",
        }));

        setData(cleanedData);
      })
      .catch((err) => console.error("Error loading Excel:", err));
  }, []);

  const uniqueDomains = [...new Set(data.map((d) => d.domain).filter(Boolean))];
  const domains = DOMAIN_ORDER.filter((domain) => uniqueDomains.includes(domain));

  const NewsTicker = () => {
      const [articles, setArticles] = useState([]);
      const tickerRef = useRef();
      const contentRef = useRef();
      const offsetRef = useRef(0);
  
      useEffect(() => {
        const fetchNews = async () => {
          const rssUrl = `https://news.google.com/rss/search?q=artifical+intelligence+risks&hl=en-US&gl=US&ceid=US:en`;
          const jsonUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;
          try {
            const res = await fetch(jsonUrl);
            const data = await res.json();
            setArticles(data.items || []);
          } catch (err) {
            console.error('Error fetching RSS feed:', err);
          }
        };
  
        fetchNews();
        const interval = setInterval(fetchNews, 12 * 60 * 60 * 1000); // Every 12 hours

        return () => clearInterval(interval);
      }, []);
  
      useEffect(() => {
        let animationFrameId;
  
        const scroll = () => {
          const content = contentRef.current;
          if (!content) return;
  
          offsetRef.current -= 1; // speed
          const contentWidth = content.scrollWidth;
          const tickerWidth = tickerRef.current?.offsetWidth || 0;
  
          if (Math.abs(offsetRef.current) >= contentWidth) {
            offsetRef.current = tickerWidth;
          }
  
          content.style.transform = `translateX(${offsetRef.current}px)`;
          animationFrameId = requestAnimationFrame(scroll);
        };
  
        animationFrameId = requestAnimationFrame(scroll);
  
        return () => cancelAnimationFrame(animationFrameId);
      }, [articles]);
  
      return (
        <div
          ref={tickerRef}
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: '#fff',
            color: '#000',
            padding: '20px 0',
            whiteSpace: 'nowrap',
            fontSize: '14px',
          }}
        >
          {/* Cyber Security Label */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '160px',
              background: '#000',
              color: '#fff',
              clipPath: 'polygon(0 0, 100% 0, 85% 100%, 0% 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              zIndex: 10,
              pointerEvents: 'auto',
              fontSize: '18px',
            }}
          >
            AI Risks
          </div>
  
          {/* Overlay to block clicks behind label */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '160px',
              zIndex: 9,
              pointerEvents: 'auto',
            }}
          />
  
          {/* Scrolling Content */}
          <div
            ref={contentRef}
            style={{
              display: 'inline-block',
              willChange: 'transform',
              paddingLeft: '180px',
            }}
          >
            {articles.map((item, i) => {
              const [titleText, source] = item.title.split(' - ');
              const date = new Date(item.pubDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
  
              return (
                <span
                  key={i}
                  style={{
                    display: 'inline-block',
                    padding: '0 20px',
                    marginLeft: i === 0 ? 0 : '10px',
                    borderLeft: i === 0 ? 'none' : '3px solid #000',
                  }}
                >
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: '#000',
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                  >
                    <span>{date}</span>
                    <span style={{ fontWeight: 'bold', margin: '0 10px' }}>
                      {titleText}
                    </span>
                    <span>{source}</span>
                  </a>
                </span>
              );
            })}
          </div>
        </div>
      );
    };

  return (
    <div className="bg">
      <div className="risk-content">
        <NewsTicker/>
        <br/>
        {domains.map((domain) => {
          const cleanDomain = normalizeDomainName(domain);
          const domainColor = DOMAIN_COLORS4[cleanDomain] || "#ccc";
          const isDomainExpanded = expandedDomain === domain;

          const domainIndex = domains.indexOf(domain);
          const expandedIndex = expandedDomain ? domains.indexOf(expandedDomain) : -1;

          let hiddenClass = "";
          if (expandedDomain && domain !== expandedDomain) {
            hiddenClass = domainIndex < expandedIndex ? "hidden-above" : "hidden-below";
          }

          const subdomains = sortSubdomains([
            ...new Set(
              data
                .filter((d) => d.domain === domain)
                .map((d) => d.subDomain)
                .filter(Boolean)
            ),
          ]);

          return (
            <button
              key={domain}
              className={`domain-button ${isDomainExpanded ? "expanded" : ""} ${hiddenClass}`}
              style={{ "--domain-color": domainColor }}
              onClick={() => {
                if (!isDomainExpanded) {
                  setExpandedDomain(domain);
                  setExpandedSubdomain(null);
                }
              }}
            >
              <h1 className="domain-header">{domain}</h1>

              {isDomainExpanded && (
                <button
                  className="arrow-close-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedDomain(null);
                    setExpandedSubdomain(null);
                  }}
                >
                  <FaArrowAltCircleUp className="arrow-close" />
                </button>
              )}

              {isDomainExpanded && (
                <div className="subdomain-container">
                  {subdomains.map((sub, i) => {
                    const subKey = `${domain}||${sub}`;
                    const isSubExpanded = expandedSubdomain === subKey;
                    const risks = data.filter(
                      (d) => d.domain === domain && d.subDomain === sub
                    );

                    const subIndex = i;
                    const expandedSubIndex = expandedSubdomain
                      ? subdomains.findIndex((s) => `${domain}||${s}` === expandedSubdomain)
                      : -1;

                    let hiddenClass = "";
                    if (expandedSubdomain && subKey !== expandedSubdomain) {
                      hiddenClass = subIndex < expandedSubIndex ? "hidden-above" : "hidden-below";
                    }

                    return (
                      <div
                        key={sub}
                        className={`subdomain-wrapper ${hiddenClass}`}
                        style={{ "--domain-color": domainColor }}
                      >
                        <button
                          className={`subdomain-button ${isSubExpanded ? "expanded" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isSubExpanded) {
                              setExpandedSubdomain(subKey);
                              setTimeout(() => {
                                document
                                  .querySelector(".subdomain-button.expanded")
                                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
                              }, 100);
                            }
                          }}
                        >
                          <h1 className="domain-header">{sub.replace(/>\s*/, "- ").trim()}</h1>
                          {isSubExpanded && (
                            <button
                              className="arrow-close-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedSubdomain(null);
                              }}
                            >
                              <FaArrowAltCircleUp className="arrow-close" />
                            </button>
                          )}
                          {isSubExpanded && (
                            <div className="risk-container">
                              
                              {risks.map((risk) => (
                                <div key={risk.id} className="risk-card">
                                  <div className="threat-card-accent" style={{ "--domain-color": domainColor }} />
                                  <h3>{risk.title}</h3>
                                  <table className="risk-detail-table" style={{ "--domain-color": domainColor }}>
                                    <tbody>
                                      <tr>
                                        <th>Risk Category</th>
                                        <td>{risk.riskCategory}</td>
                                      </tr>
                                      <tr>
                                        <th>Description</th>
                                        <td>{risk.description}</td>
                                      </tr>
                                      <tr>
                                        <th>Additional Evidence</th>
                                        <td>{risk.evidence}</td>
                                      </tr>
                                      <tr>
                                        <th>Entity</th>
                                        <td>{risk.entity}</td>
                                      </tr>
                                      <tr>
                                        <th>Intent</th>
                                        <td>{risk.intent}</td>
                                      </tr>
                                      <tr>
                                        <th>Timing</th>
                                        <td>{risk.timing}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              ))}
                            </div>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AIRiskDatabase;
